import { useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { AlertTriangle, Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

const KINDS = [
  { v: "damage", l: "Damage to the car" },
  { v: "late_return", l: "Late return / no return" },
  { v: "no_show", l: "No-show at pickup" },
  { v: "cleanliness", l: "Cleanliness / condition" },
  { v: "safety", l: "Safety concern" },
  { v: "other", l: "Other" },
];

export default function DisputeDialog({ bookingId, trigger, onCreated }) {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState("damage");
  const [desc, setDesc] = useState("");
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name} too large`); continue; }
        const fd = new FormData(); fd.append("file", f);
        const { data } = await api.post(`/uploads?kind=doc`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(data.path);
      }
      setPhotos((p) => [...p, ...uploaded]);
    } catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (desc.trim().length < 5) { toast.error("Add a short description"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/disputes", {
        booking_id: bookingId, kind, description: desc, photo_paths: photos,
      });
      toast.success("Dispute opened — our team will review it");
      setOpen(false);
      setKind("damage"); setDesc(""); setPhotos([]);
      onCreated?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to open dispute");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bg-[#131722] border-slate-800 max-w-md" data-testid="dispute-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2 text-white">
            <AlertTriangle className="w-5 h-5 text-amber-400" /> Something's wrong?
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-400 mt-1">Open a dispute. We'll pause payouts and review within 2 hours.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-3">
          <div>
            <Label>What happened?</Label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger data-testid="dispute-kind" className="bg-slate-900 border-slate-800 mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#131722] border-slate-800">{KINDS.map((k) => <SelectItem key={k.v} value={k.v}>{k.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Describe the issue</Label>
            <Textarea required value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="dispute-desc"
              placeholder="Tell us what happened and any relevant details…"
              className="bg-slate-900 border-slate-800 mt-1.5 min-h-[120px]" />
          </div>
          <div>
            <Label>Photos (optional)</Label>
            <label className="mt-1.5 flex items-center justify-center gap-2 h-11 rounded-lg border border-dashed border-slate-700 bg-slate-900/50 cursor-pointer hover:border-slate-600">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4" /> Add photos</>}
              <input type="file" multiple accept="image/*" className="hidden" onChange={upload} data-testid="dispute-photos" />
            </label>
            {photos.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {photos.map((p, i) => (
                  <div key={p} className="flex items-center gap-1 px-2 py-1 rounded bg-slate-800 text-xs font-mono">
                    photo-{i + 1}
                    <button type="button" onClick={() => setPhotos((ps) => ps.filter((x) => x !== p))} data-testid={`remove-photo-${i}`}>
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <Button type="submit" disabled={busy} data-testid="dispute-submit"
            className="w-full h-11 bg-amber-500 text-slate-950 hover:bg-amber-400 font-semibold">
            {busy ? "Opening ticket…" : "Open dispute"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
